import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  FileText, ShoppingCart, Trash2, Building2, Package,
  Truck, CheckCircle, AlertTriangle, Search, ChevronDown, ChevronUp, Send, ChevronRight
} from 'lucide-react';
import api from '../lib/api';
import { useCart } from '../context/CartContext';
import RetailerLayout from '../components/RetailerLayout';
import { toast } from 'sonner';

const fmtPrice = (v) => v != null ? `${Number(v).toFixed(2).replace('.', ',')} \u20ac` : '\u2014';

const PreparationPage = () => {
  const cart = useCart();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [simulating, setSimulating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [expandedPartners, setExpandedPartners] = useState({});

  // Extract simulation data from cart
  const simData = cart.cart?.last_simulation;
  const results = simData?.results || {};
  const simErrors = simData?.errors || {};

  useEffect(() => {
    api.get('/reference/shipping-zones').then(r => {
      const z = r.data || [];
      setZones(z);
      if (!cart.shippingZone && z.length > 0) {
        cart.setShippingZone(z[0].value);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Search products
  const searchProducts = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/products?search=${encodeURIComponent(q)}&limit=15`);
      const raw = res.data || [];
      setSearchResults(Array.isArray(raw) ? raw : (raw?.products || []));
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchProducts(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery, searchProducts]);

  // Add product from search
  const addFromSearch = async (product, dist) => {
    await cart.addItem({
      product_id: product.id,
      product_name: product.intitule,
      product_image: (product.images || [])[0],
      distribution_id: dist.id,
      partner_id: dist.partner_id,
      partner_nom: dist.partner_nom || 'Distributeur',
      base_price: dist.purchase_price || 0,
      quantity: Math.max(dist.multiple_commande || 1, dist.minimum_commande || 1),
      min_qty: dist.minimum_commande,
      max_qty: dist.maximum_commande,
      multiple: dist.multiple_commande,
    });
    setSearchQuery('');
    setSearchResults([]);
  };

  // Simulate all groups via cart API
  const simulateAll = async () => {
    if (!cart.shippingZone) { cart.requestZone(); return; }
    if (!cart.cart?.id) return;
    setSimulating(true);
    try {
      await api.post(`/carts/${cart.cart.id}/simulate`);
      await cart.fetchCart(); // Reload cart with simulation results
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) { toast.error(detail); }
    }
    setSimulating(false);
  };

  const togglePartner = (pid) => setExpandedPartners(prev => ({ ...prev, [pid]: !prev[pid] }));

  // Submit cart → create PurchaseOrders
  const submitOrder = async () => {
    if (!hasResults) { toast.error('Calculez les totaux avant de valider'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/purchase-orders/from-cart');
      const data = res.data;
      toast.success(data.message || 'Commande validée');
      // Refresh cart (will get new empty draft)
      await cart.fetchCart();
      // Navigate to orders page
      navigate('/dashboard/profil?tab=orders');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la validation');
    }
    setSubmitting(false);
  };

  // Global totals from simulation results
  const globalSubtotal = Object.values(results).reduce((s, r) => s + (r?.summary?.subtotal || 0), 0);
  const globalShipping = Object.values(results).reduce((s, r) => s + (r?.summary?.shipping_cost || 0), 0);
  const globalTotal = Object.values(results).reduce((s, r) => s + (r?.summary?.total || 0), 0);
  const hasResults = Object.keys(results).length > 0;

  return (
    <RetailerLayout>
    <div className="max-w-5xl mx-auto space-y-6 pb-8 px-4" data-testid="preparation-page">
      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <FileText className="h-6 w-6 text-slate-600" />
          <div>
            <h1 className="text-xl font-bold text-slate-900">Simulation des bons de commande</h1>
            <p className="text-sm text-slate-500">Estimez le co&ucirc;t de vos commandes par distributeur</p>
          </div>
        </div>
        <Link to="/dashboard/profil?tab=orders" className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors" data-testid="nav-to-orders">
          <FileText className="h-4 w-4" /> Mes commandes <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      {/* ZONE SELECTOR */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
        <div className="flex-1 w-full sm:w-auto">
          <Label className="text-xs font-semibold text-slate-600 mb-1 block">Zone de livraison</Label>
          <Select value={cart.shippingZone || undefined} onValueChange={cart.setShippingZone}>
            <SelectTrigger data-testid="prep-zone-select"><SelectValue placeholder="Choisir une zone" /></SelectTrigger>
            <SelectContent>
              {zones.map(z => <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {cart.totalItems > 0 && (
          <Button onClick={simulateAll} disabled={simulating || !cart.shippingZone}
            className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white mt-2 sm:mt-6"
            data-testid="prep-simulate-btn">
            {simulating ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <FileText className="h-4 w-4" />}
            Calculer les totaux
          </Button>
        )}
      </div>

      {/* PRODUCT SEARCH */}
      <div className="relative">
        <Label className="text-xs font-semibold text-slate-600 mb-1 block">Ajouter un produit</Label>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un produit..." className="pl-9" data-testid="prep-product-search" />
        </div>
        {searchResults.length > 0 && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto" data-testid="prep-search-dropdown">
            {searchResults.map(p => {
              const dists = (p.distributions || []).filter(d => d.purchase_price > 0);
              if (dists.length === 0) return null;
              return dists.map(d => (
                <button key={`${p.id}-${d.id}`} type="button" onClick={() => addFromSearch(p, d)}
                  className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center gap-3 border-b border-slate-100"
                  data-testid={`prep-search-item-${p.id?.substring(0,8)}-${d.partner_id?.substring(0,8)}`}>
                  {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-8 h-8 object-cover rounded" /> : <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center"><Package className="h-3 w-3 text-slate-300" /></div>}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{p.intitule}</p>
                    <p className="text-xs text-slate-400">{d.partner_nom} &middot; {fmtPrice(d.purchase_price)}/u</p>
                  </div>
                </button>
              ));
            })}
          </div>
        )}
      </div>

      {/* DISTRIBUTOR GROUPS */}
      {cart.groupsList.length > 0 ? (
        <div className="space-y-4">
          {cart.groupsList.map(group => {
            const res = results[group.partner_id];
            const err = simErrors[group.partner_id];
            const isExpanded = expandedPartners[group.partner_id] !== false;
            return (
              <PartnerGroup key={group.partner_id} group={group} result={res} error={err}
                isExpanded={isExpanded} onToggle={() => togglePartner(group.partner_id)}
                onRemoveItem={(idx) => cart.removeItem(group.partner_id, idx)}
                onUpdateQty={(idx, qty) => cart.updateItemQty(group.partner_id, idx, qty)}
                onClear={() => cart.clearPartner(group.partner_id)}
              />
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <ShoppingCart className="h-12 w-12 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Recherchez et ajoutez des produits pour pr&eacute;parer une commande</p>
        </div>
      )}

      {/* GLOBAL SUMMARY */}
      {hasResults && (
        <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50/30 p-5 space-y-3" data-testid="global-summary">
          <h2 className="text-base font-bold text-indigo-900 flex items-center gap-2">
            <FileText className="h-5 w-5" /> R&eacute;capitulatif global
          </h2>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Distributeurs</p>
              <p className="text-lg font-bold text-slate-900">{Object.keys(results).length}</p>
            </div>
            <div>
              <p className="text-slate-500">Produits</p>
              <p className="text-lg font-bold text-slate-900">{cart.totalItems}</p>
            </div>
            <div>
              <p className="text-slate-500">Montant global HT</p>
              <p className="text-lg font-bold text-indigo-700">{fmtPrice(globalTotal)}</p>
            </div>
          </div>
          <div className="pt-2 border-t border-indigo-200 flex flex-col sm:flex-row justify-between text-sm gap-1">
            <span className="text-slate-600">Sous-total produits : {fmtPrice(globalSubtotal)}</span>
            <span className="text-slate-600">Frais de port estim&eacute;s : {fmtPrice(globalShipping)}</span>
          </div>
          <div className="pt-3 flex justify-end">
            <Button onClick={submitOrder} disabled={submitting}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-6"
              data-testid="submit-order-btn">
              {submitting ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Send className="h-4 w-4" />}
              Générer les bons de commande
            </Button>
          </div>
        </div>
      )}
    </div>
    </RetailerLayout>
  );

/** Per-distributor section */
};
const PartnerGroup = ({ group, result, error, isExpanded, onToggle, onRemoveItem, onUpdateQty, onClear }) => {
  const summary = result?.summary;
  const items = result?.items || [];

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid={`partner-group-${group.partner_id?.substring(0,8)}`}>
      {/* Header */}
      <button type="button" onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 border-b border-slate-200 text-left">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <span className="text-sm font-bold text-slate-900 truncate">{group.partner_nom}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">{group.items.length} produit{group.items.length > 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {summary && (
            <div className="text-right hidden sm:block">
              <span className="text-sm font-bold text-indigo-700">{fmtPrice(summary.total)}</span>
              {summary.franco_reached ? (
                <span className="block text-[10px] text-emerald-600"><CheckCircle className="h-3 w-3 inline" /> Franco</span>
              ) : summary.franco_threshold ? (
                <span className="block text-[10px] text-amber-600">reste {fmtPrice(summary.franco_remaining)}</span>
              ) : null}
            </div>
          )}
          {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </button>

      {isExpanded && (
        <div className="p-4 space-y-3">
          {/* Error */}
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}

          {/* Warnings from simulation engine */}
          {result?.warnings?.length > 0 && (
            <div className="space-y-1" data-testid={`partner-warnings-${group.partner_id?.substring(0,8)}`}>
              {result.warnings.map((w, wi) => (
                <div key={wi} className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700 flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {w.message}
                </div>
              ))}
            </div>
          )}

          {/* Items */}
          <div className="space-y-2">
            {group.items.map((item, idx) => {
              const computed = items.find(ci => ci.product_id === item.product_id);
              return (
                <div key={`${item.product_id}-${idx}`} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg border border-slate-100"
                  data-testid={`prep-item-${group.partner_id?.substring(0,8)}-${idx}`}>
                  {item.product_image ? <img src={item.product_image} alt="" className="w-10 h-10 object-cover rounded flex-shrink-0" /> : <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center flex-shrink-0"><Package className="h-4 w-4 text-slate-300" /></div>}
                  <div className="flex-1 min-w-0">
                    <Link to={`/dashboard/produit/${item.product_id}`} className="text-sm font-medium text-indigo-700 hover:text-indigo-900 hover:underline truncate block" data-testid={`cart-product-link-${idx}`}>
                      {item.product_name}
                    </Link>
                    <p className="text-xs text-slate-400">{fmtPrice(item.base_price)}/u</p>
                  </div>
                  <Input type="number" min={item.min_qty || 1} step={item.multiple || 1}
                    value={item.quantity} onChange={(e) => onUpdateQty(idx, e.target.value)}
                    className="w-20 h-8 text-center text-sm tabular-nums" data-testid={`prep-qty-${idx}`} />
                  {computed && (
                    <div className="text-right min-w-[80px] hidden sm:block">
                      <p className="text-sm font-semibold text-slate-900 tabular-nums">{fmtPrice(computed.total_price_paid)}</p>
                      {computed.free_units > 0 && <p className="text-[10px] text-emerald-600">+{computed.free_units} offert{computed.free_units > 1 ? 's' : ''}</p>}
                    </div>
                  )}
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 flex-shrink-0"
                    onClick={() => onRemoveItem(idx)} data-testid={`prep-remove-${idx}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Partner summary */}
          {summary && (
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2 text-sm" data-testid={`partner-summary-${group.partner_id?.substring(0,8)}`}>
              <div className="flex justify-between"><span className="text-slate-600">Sous-total</span><span className="font-semibold tabular-nums">{fmtPrice(summary.subtotal)}</span></div>
              {summary.franco_threshold != null && (
                <div className={`flex items-center justify-between p-2 rounded-lg ${summary.franco_reached ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`} data-testid={`franco-status-${group.partner_id?.substring(0,8)}`}>
                  {summary.franco_reached ? (
                    <><span className="flex items-center gap-1 text-emerald-700"><CheckCircle className="h-4 w-4" /> Franco atteint</span><span className="font-semibold text-emerald-700">{fmtPrice(summary.franco_threshold)}</span></>
                  ) : (
                    <><span className="flex items-center gap-1 text-amber-700"><AlertTriangle className="h-4 w-4" /> Franco non atteint (reste {fmtPrice(summary.franco_remaining)})</span><span className="font-semibold text-amber-700">{fmtPrice(summary.franco_threshold)}</span></>
                  )}
                </div>
              )}
              <div className="flex justify-between">
                <span className="flex items-center gap-1 text-slate-600"><Truck className="h-4 w-4" /> Frais de port</span>
                <span className={`font-semibold tabular-nums ${summary.shipping_cost === 0 ? 'text-emerald-600' : 'text-slate-900'}`}>
                  {summary.shipping_cost === 0 ? 'Gratuit' : fmtPrice(summary.shipping_cost)}
                </span>
              </div>
              <div className="flex justify-between pt-1 border-t border-slate-200">
                <span className="font-bold text-slate-900">Total {group.partner_nom}</span>
                <span className="font-black text-lg text-indigo-700 tabular-nums">{fmtPrice(summary.total)}</span>
              </div>
            </div>
          )}

          {/* Clear partner */}
          <div className="flex items-center justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={onClear} className="text-xs text-red-400 hover:text-red-600 gap-1">
              <Trash2 className="h-3 w-3" /> Retirer ce distributeur
            </Button>
            {summary && (
              <Button size="sm" className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-4"
                data-testid={`send-to-partner-${group.partner_id?.substring(0,8)}`}>
                <Send className="h-3.5 w-3.5" /> Envoyer au distributeur
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PreparationPage;
// Backwards compat alias
export { PreparationPage as SimulationCartPage };
